// Author: Ahsan Saleem
/**
 * The BloomOS shell — one connected operating environment. The Bloom mark opens
 * the Launcher and carries the system's live state; the rail groups applications
 * by workspace (Operate / Observe / Analyse / System) with Cmd+1–4; the system
 * bar hosts BloomShell, the Control and Notification centres and the session;
 * the canvas restores its scroll per route. Mobile keeps a thumb dock.
 */
import { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState,
  type ReactNode, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import * as Tooltip from '@radix-ui/react-tooltip';
import {
  Search, Command as CommandIcon, FlaskConical, MoreHorizontal, X, ChevronRight, ChevronLeft,
  Sprout, Settings2, LogOut, LayoutGrid, Pin,
} from 'lucide-react';
import { auth, siteSource } from '../api.js';
import { WORKSPACES, visibleWorkspaces, workspaceOf, session, INSPECTOR_MIN, INSPECTOR_MAX } from '../session.js';
import { effectiveTheme, toggleTheme } from './theme.js';
import { Bloom } from './Bloom.js';
import { Glyph } from './glyphs.js';
import { useSystem } from './system.js';
import { CommandPalette } from './CommandPalette.js';
import { Launcher } from './Launcher.js';
import { ControlCentre, NotificationCentre } from './Centres.js';
import { Toaster } from './toast.js';
import { InsightsCentre } from '../v2/ai.js';

/**
 * The Inspector is BloomOS's core surface: selecting any object opens it *beside*
 * the work, not over it — the canvas stays live. Opening a second object stacks it
 * (Back walks the history), so drilling through related objects never costs you the
 * screen behind. On phones — no room for a side dock — it falls back to a modal sheet.
 */
interface Frame { key: number; title: ReactNode; node: ReactNode; }
interface InspectorApi { open: (title: ReactNode, node: ReactNode, from?: DOMRect) => void; close: () => void; back: () => void; }
const InspectorCtx = createContext<InspectorApi>({ open: () => {}, close: () => {}, back: () => {} });
export const useInspector = () => useContext(InspectorCtx);

/** Reactive media-query match — drives the dock↔sheet responsive split. */
function useMediaQuery(query: string): boolean {
  const [match, setMatch] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatch(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return match;
}

const DOCK = [
  { to: '/v2', label: 'Pulse', glyph: 'pulse' },
  { to: '/v2/work', label: 'Today', glyph: 'today' },
  { to: '/v2/record', label: 'Record', glyph: 'record' },
  { to: '/v2/alerts', label: 'Alerts', glyph: 'alerts' },
];

const openLauncher = () => window.dispatchEvent(new Event('bloomos-open-launcher'));

/** Turn a membership role enum (e.g. SITE_MANAGER) into a readable label. */
const roleLabel = (r?: string) =>
  r ? r.toLowerCase().split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '';

export function Shell() {
  const [frames, setFrames] = useState<Frame[]>([]);
  const [moreOpen, setMoreOpen] = useState(false);
  const [inspW, setInspW] = useState(() => session.inspectorWidth());
  const [pinned, setPinned] = useState(() => session.inspectorPinned());
  const [resizing, setResizing] = useState(false);
  const docked = useMediaQuery('(min-width: 900px)');
  const seq = useRef(0);
  const dockRef = useRef<HTMLElement>(null);
  const inspWRef = useRef(inspW); inspWRef.current = inspW;
  const pinnedRef = useRef(pinned); pinnedRef.current = pinned;
  const prevDepth = useRef(0);
  const { bloomState, site, snapshot, user } = useSystem();
  const workspaces = useMemo(() => visibleWorkspaces(user?.capabilities), [user?.capabilities]);
  const loc = useLocation();
  const navigate = useNavigate();
  const theme = effectiveTheme();
  const canvasRef = useRef<HTMLElement>(null);
  const activeWs = workspaceOf(loc.pathname);

  const open = useCallback((title: ReactNode, node: ReactNode) => {
    setFrames((f) => [...f, { key: ++seq.current, title, node }].slice(-12));
  }, []);
  const close = useCallback(() => setFrames([]), []);
  const back = useCallback(() => setFrames((f) => (f.length > 1 ? f.slice(0, -1) : [])), []);
  const api = useMemo<InspectorApi>(() => ({ open, close, back }), [open, close, back]);
  const top = frames[frames.length - 1];

  const togglePin = () => setPinned((p) => { session.setInspectorPinned(!p); return !p; });

  // The dock is a persistent surface. On a plain navigation an unpinned object is
  // released (it belonged to the screen you left); a pinned object follows you.
  useEffect(() => { if (!pinnedRef.current) setFrames([]); }, [loc.pathname]);

  // Land keyboard focus in the inspector when a new object opens — but never trap:
  // the canvas stays live beside it. This is a panel, not a modal.
  useEffect(() => {
    if (docked && frames.length > prevDepth.current) dockRef.current?.focus();
    prevDepth.current = frames.length;
  }, [frames.length, docked]);

  // Drag the seam to resize; the width is remembered per operator (spatial memory).
  const startResize = (e: ReactPointerEvent) => {
    e.preventDefault();
    setResizing(true);
    const onMove = (ev: PointerEvent) =>
      setInspW(Math.min(INSPECTOR_MAX, Math.max(INSPECTOR_MIN, window.innerWidth - ev.clientX)));
    const onUp = () => {
      setResizing(false);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      session.setInspectorWidth(inspWRef.current);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // Session memory: record the route; restore this route's scroll position.
  useEffect(() => {
    session.setPath(loc.pathname);
    const el = canvasRef.current;
    if (el) el.scrollTop = session.getScroll(loc.pathname);
  }, [loc.pathname]);

  // Cmd/Ctrl+1–4 → switch workspace (its first application).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && /^[1-9]$/.test(e.key)) {
        const w = workspaces.find((x) => x.short === e.key);
        if (w && w.apps[0]) { e.preventDefault(); navigate(w.apps[0].to); }
      } else if ((e.metaKey || e.ctrlKey) && e.key === '\\') {
        e.preventDefault(); setFrames((f) => (f.length ? [] : f));  // ⌘\ dismisses the inspector
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, workspaces]);

  return (
    <Tooltip.Provider delayDuration={350}>
      <InspectorCtx.Provider value={api}>
        <div className={`shell${resizing ? ' resizing' : ''}`}
          data-inspector={docked && top ? 'open' : 'closed'}
          style={{ '--inspector-w': `${docked && top ? inspW : 0}px` } as CSSProperties}>
          <aside className="rail" aria-label="Applications">
            <button className="rail-bloom" onClick={openLauncher} aria-label="Open Bloom Launcher">
              <Bloom size={26} state={bloomState} />
            </button>
            <nav className="rail-groups">
              {workspaces.map((w) => (
                <div key={w.id} className={`rail-group${w.id === activeWs ? ' active' : ''}`}>
                  {w.apps.map((a) => <RailLink key={a.to} to={a.to} label={a.label} glyph={a.glyph} />)}
                </div>
              ))}
            </nav>
            <div className="rail-foot">
              <button className="rail-bloom sm" onClick={openLauncher} aria-label="Applications"><LayoutGrid size={18} /></button>
            </div>
          </aside>

          <div className="main">
            <header className="topbar">
              <div className="tb-left">
                <span className="tb-ws label">{WORKSPACES.find((w) => w.id === activeWs)?.label}</span>
                <button className="ctx" onClick={() => navigate('/v2/canvas')} aria-label="Facility context">
                  <span className="ctx-site">{site?.name ?? auth.siteName ?? 'BloomOS'}</span>
                  <ChevronRight size={13} aria-hidden className="ctx-sep" />
                  <span className="ctx-scope">{snapshot ? `${snapshot.zones.length} zone${snapshot.zones.length === 1 ? '' : 's'}` : '—'}</span>
                </button>
              </div>

              <button className="cmd-open" onClick={() => window.dispatchEvent(new Event('bloomos-open-cmdk'))} aria-label="Open BloomShell">
                <Search size={15} aria-hidden /><span>BloomShell — search or run…</span>
                <kbd><CommandIcon size={11} aria-hidden />K</kbd>
              </button>

              <div className="sys">
                {snapshot && siteSource(snapshot.latest) === 'SIMULATED' && <SimIndicator />}
                <InsightsCentre />
                <ControlCentre />
                <NotificationCentre />
                <Clock />
                <button className="theme-btn" onClick={toggleTheme} aria-label={`Switch to ${theme === 'command' ? 'Field' : 'Command'} mode`}>
                  {theme === 'command' ? 'Command' : 'Field'}
                </button>
                <UserMenu />
              </div>
            </header>

            <main className="canvas" ref={canvasRef} key={loc.pathname}
              onScroll={(e) => session.setScroll(loc.pathname, (e.target as HTMLElement).scrollTop)}>
              <Outlet />
            </main>
          </div>

          {/* Desktop: a persistent dock beside the canvas — never an overlay. */}
          {docked && top && (
            <aside className="inspector-dock" ref={dockRef} tabIndex={-1} aria-label="Inspector"
              onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); frames.length > 1 ? back() : close(); } }}>
              <span className="idock-resize" aria-hidden onPointerDown={startResize} />
              <header className="idock-head">
                <div className="idock-lead">
                  {frames.length > 1 && (
                    <button className="idock-nav" onClick={back} aria-label={`Back to previous object (${frames.length - 1} behind)`}>
                      <ChevronLeft size={17} aria-hidden />
                    </button>
                  )}
                  <span className="idock-eyebrow"><span className="idock-live" aria-hidden />Inspector
                    {frames.length > 1 && <span className="idock-depth tnum" aria-hidden>{frames.length}</span>}</span>
                </div>
                <div className="idock-tools">
                  <button className={`idock-tool${pinned ? ' on' : ''}`} aria-pressed={pinned} onClick={togglePin}
                    aria-label={pinned ? 'Unpin — release the inspector when you navigate' : 'Pin — keep this object open across screens'}>
                    <Pin size={14} aria-hidden />
                  </button>
                  <button className="idock-tool" onClick={close} aria-label="Close inspector"><X size={16} aria-hidden /></button>
                </div>
              </header>
              <h2 className="idock-title">{top.title}</h2>
              <div className="idock-body">{top.node}</div>
            </aside>
          )}

          {/* Phones: no room to dock — a focused modal sheet, driven by the same stack. */}
          {!docked && (
            <Dialog.Root open={!!top} onOpenChange={(o) => !o && close()}>
              <Dialog.Portal>
                <Dialog.Overlay className="sheet-overlay" />
                <Dialog.Content className="inspector" aria-describedby={undefined}>
                  <header className="inspector-head">
                    <div className="insp-lead">
                      {frames.length > 1 && <button className="btn btn-icon btn-ghost" onClick={back} aria-label="Back"><ChevronLeft size={18} /></button>}
                      <Dialog.Title className="inspector-title">{top?.title}</Dialog.Title>
                    </div>
                    <Dialog.Close className="btn btn-icon btn-ghost" aria-label="Close"><X size={18} /></Dialog.Close>
                  </header>
                  <div className="inspector-body">{top?.node}</div>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>
          )}

          <nav className="dock" aria-label="Primary">
            {DOCK.map((n) => (
              <NavLink key={n.to} to={n.to} className="dock-item">
                <Glyph name={n.glyph} size={21} /><span>{n.label}</span>
              </NavLink>
            ))}
            <button className="dock-item" onClick={openLauncher} aria-label="Launcher"><MoreHorizontal size={21} aria-hidden /><span>Apps</span></button>
          </nav>

          <MoreSheet open={moreOpen} onOpenChange={setMoreOpen} theme={theme} />
          <Launcher />
          <CommandPalette />
          <Toaster />
        </div>
      </InspectorCtx.Provider>
    </Tooltip.Provider>
  );
}

function RailLink({ to, label, glyph }: { to: string; label: string; glyph: string }) {
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <NavLink to={to} className="rail-link" aria-label={label}><Glyph name={glyph} size={20} /></NavLink>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content side="right" className="tip" sideOffset={10}>{label}<Tooltip.Arrow className="tip-arrow" /></Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

function SimIndicator() {
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <span className="sim-ind" role="status"><FlaskConical size={13} aria-hidden /> Simulated</span>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content side="bottom" className="tip" sideOffset={8}>
          Virtual Facility — telemetry is SIMULATED, not live hardware.<Tooltip.Arrow className="tip-arrow" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

function Clock() {
  const [t, setT] = useState(() => new Date());
  useEffect(() => { const id = setInterval(() => setT(new Date()), 30_000); return () => clearInterval(id); }, []);
  return <span className="clock tnum" aria-hidden>{t.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>;
}

function UserMenu() {
  const navigate = useNavigate();
  const { user, site } = useSystem();
  const name = user?.name || 'Operator';
  const initials = (user?.name || '').trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase() || 'BO';
  const subtitle = [roleLabel(user?.role), site?.name].filter(Boolean).join(' · ') || 'BloomLabs';
  return (
    <Dialog.Root>
      <Dialog.Trigger asChild><button className="user" aria-label="Session">{initials}</button></Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="sheet-overlay" />
        <Dialog.Content className="user-sheet" aria-describedby={undefined}>
          <Dialog.Title className="centre-title">{name}</Dialog.Title>
          <div className="user-org">{subtitle}</div>
          <button className="btn" onClick={() => { navigate('/v2/settings'); }}><Settings2 size={16} /> Settings</button>
          <button className="btn" onClick={() => { auth.token = null; auth.siteId = null; auth.siteName = null; sessionStorage.removeItem('bloomos_booted'); navigate('/login'); }}><LogOut size={16} /> Sign out</button>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function MoreSheet({ open, onOpenChange, theme }: { open: boolean; onOpenChange: (o: boolean) => void; theme: string }) {
  const navigate = useNavigate();
  const go = (p: string) => { navigate(p); onOpenChange(false); };
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="sheet-overlay" />
        <Dialog.Content className="more-sheet">
          <Dialog.Title className="sheet-grip-title">Applications</Dialog.Title>
          <div className="more-grid">
            <button className="more-cell" onClick={() => go('/v2/canvas')}><Glyph name="facility" size={20} /><span>Facility</span></button>
            <button className="more-cell" onClick={() => go('/v2/production')}><Sprout size={20} /><span>Production</span></button>
            {import.meta.env.DEV && <button className="more-cell" onClick={() => go('/simulation')}><Glyph name="sim" size={20} /><span>Simulation</span></button>}
            <button className="more-cell" onClick={() => go('/v2/settings')}><Settings2 size={20} /><span>Settings</span></button>
            <button className="more-cell" onClick={() => { toggleTheme(); }}><Glyph name="pulse" size={20} /><span>{theme === 'command' ? 'Field mode' : 'Command mode'}</span></button>
            <button className="more-cell" onClick={() => { auth.token = null; auth.siteId = null; sessionStorage.removeItem('bloomos_booted'); go('/login'); }}><LogOut size={20} /><span>Sign out</span></button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
